import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Space,
  Tag,
  Image,
  Typography,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tooltip,
  Divider,
  Progress,
  Spin,
  Upload,
  Select,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  HeartOutlined,
  MessageOutlined,
  ShareAltOutlined,
  PlayCircleOutlined,
  DownloadOutlined,
  BarChartOutlined,
  UploadOutlined,
  ImportOutlined,
  DownOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text } = Typography;
const { TextArea } = Input;

const VideoTracking = () => {
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [batchModalVisible, setBatchModalVisible] = useState(false);
  const [commentsModalVisible, setCommentsModalVisible] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [form] = Form.useForm();
  const [batchForm] = Form.useForm();
  const [batchVideoUrls, setBatchVideoUrls] = useState('');
  const [timeFilter, setTimeFilter] = useState('all');
  const [useAsyncImport, setUseAsyncImport] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);


  useEffect(() => {
    loadData();
  }, [timeFilter]);

  const loadData = async () => {
    await Promise.all([
      loadVideos(),
      loadStats()
    ]);
  };

  const loadVideos = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/video-tracking/videos', {
        headers: { Authorization: `Bearer ${token}` },
        params: { time_filter: timeFilter }
      });
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error('Error loading videos:', error);
      message.error('Lỗi khi tải danh sách videos: ' + (error.response?.data?.detail || error.message));
    }
  };


  const loadStats = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/video-tracking/stats', {
        headers: { Authorization: `Bearer ${token}` },
        params: { time_filter: timeFilter }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleAddVideo = async (values) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/video-tracking/add', {
        video_url: values.video_url
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success('Video đã được thêm vào danh sách theo dõi!');
        setAddModalVisible(false);
        form.resetFields();
        loadData();
      } else {
        message.error(response.data.error || 'Lỗi khi thêm video');
      }
    } catch (error) {
      message.error('Lỗi khi thêm video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleBatchImport = () => {
    batchForm.resetFields();
    setBatchVideoUrls('');
    setBatchModalVisible(true);
  };

  const handleBatchVideoUrlsChange = (e) => {
    const value = e.target.value;
    setBatchVideoUrls(value);
    
    // Count video URLs
    const urlLines = value.split('\n').filter(line => line.trim() !== '');
    const urlCount = urlLines.length;
    
    // Update form field for validation
    batchForm.setFieldsValue({ video_urls: value });
  };

  const handleFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      setBatchVideoUrls(content);
      batchForm.setFieldsValue({ video_urls: content });
    };
    reader.readAsText(file);
    return false; // Prevent upload
  };

  const handleBatchSubmit = async (values) => {
    try {
      const videoUrls = values.video_urls.split('\n').filter(line => line.trim() !== '');
      
      if (videoUrls.length === 0) {
        message.error('Vui lòng nhập ít nhất một video URL');
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Show progress message for large batches
      if (videoUrls.length > 5) {
        message.loading(`Đang xử lý ${videoUrls.length} video URLs... (có thể mất ${Math.ceil(videoUrls.length * 1.5)} giây)`, 0);
      }
      
      // Choose between sync and async import
      const endpoint = useAsyncImport ? '/api/video-tracking/batch-import-async' : '/api/video-tracking/batch-import';
      
      // Send batch request with rate limiting
      const response = await axios.post(endpoint, videoUrls, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        if (useAsyncImport) {
          // Async import response
          const { task_id, total_videos, estimated_time } = response.data;
          message.success(`Đã bắt đầu import ${total_videos} video URLs trong background. Task ID: ${task_id} (Ước tính: ${estimated_time})`);
          setBatchModalVisible(false);
          batchForm.resetFields();
          setBatchVideoUrls('');
          // Don't reload immediately for async import
          setTimeout(() => loadData(), 5000); // Reload after 5 seconds
        } else {
          // Sync import response với phân loại mới
          const { success_count, warning_count, error_count, errors, warnings } = response.data;
          
          // Hiển thị kết quả chi tiết
          if (warning_count > 0 && error_count === 0) {
            message.success(`Import hoàn thành: ${success_count} video mới, ${warning_count} video đã tồn tại`);
          } else if (error_count > 0 && success_count === 0 && warning_count === 0) {
            message.error(`Import thất bại: ${error_count} lỗi`);
          } else if (success_count > 0 || warning_count > 0) {
            message.success(`Import hoàn thành: ${success_count} video mới, ${warning_count} video đã tồn tại, ${error_count} lỗi`);
          } else {
            message.error(`Import thất bại: ${error_count} lỗi`);
          }
          
          // Log chi tiết
          if (errors && errors.length > 0) {
            console.log('Import errors:', errors);
          }
          if (warnings && warnings.length > 0) {
            console.log('Import warnings (videos already exist):', warnings);
          }
          
          setBatchModalVisible(false);
          batchForm.resetFields();
          setBatchVideoUrls('');
          loadData(); // Reload videos
        }
      } else {
        message.error(response.data.message || 'Có lỗi xảy ra khi import');
      }
    } catch (error) {
      console.error('Error batch importing:', error);
      if (error.response?.data?.detail) {
        message.error(`Lỗi: ${error.response.data.detail}`);
      } else {
        message.error('Có lỗi xảy ra khi import video URLs');
      }
    } finally {
      setLoading(false);
      message.destroy(); // Clear loading message
    }
  };

  const handleUpdateVideo = async (videoId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`/api/video-tracking/update/${videoId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const changes = response.data.changes;
        const changeText = Object.entries(changes)
          .filter(([_, value]) => value > 0)
          .map(([key, value]) => `${key}: +${value}`)
          .join(', ');
        
        message.success(`Cập nhật thành công! ${changeText ? `Thay đổi: ${changeText}` : ''}`);
        loadVideos();
      } else {
        message.error(response.data.error || 'Lỗi khi cập nhật video');
      }
    } catch (error) {
      message.error('Lỗi khi cập nhật video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleUpdateAllVideos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const response = await axios.post('/api/video-tracking/update-all', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const { updated, error_count } = response.data;
        if (updated > 0) {
          message.success(`Đã cập nhật thành công ${updated} videos`);
        } else {
          message.info('Không có video nào cần cập nhật');
        }

        if (error_count > 0) {
          message.warning(`Có ${error_count} video cập nhật thất bại`);
        }

        loadData();
      } else {
        message.error(response.data.message || 'Có lỗi xảy ra khi cập nhật');
      }
    } catch (error) {
      message.error('Lỗi khi cập nhật tất cả videos: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };


  const handleRemoveVideo = async (videoId) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.delete(`/api/video-tracking/remove/${videoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success('Video đã được xóa khỏi danh sách theo dõi');
        loadData();
      } else {
        message.error(response.data.error || 'Lỗi khi xóa video');
      }
    } catch (error) {
      message.error('Lỗi khi xóa video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Vui lòng chọn ít nhất một video để xóa');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      
      // Xóa từng video đã chọn
      const deletePromises = selectedRowKeys.map(videoId => 
        axios.delete(`/api/video-tracking/remove/${videoId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      const results = await Promise.allSettled(deletePromises);
      
      let successCount = 0;
      let errorCount = 0;
      
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' && result.value.data.success) {
          successCount++;
        } else {
          errorCount++;
        }
      });

      if (successCount > 0) {
        message.success(`Đã xóa thành công ${successCount} video(s)`);
      }
      if (errorCount > 0) {
        message.error(`Không thể xóa ${errorCount} video(s)`);
      }

      // Clear selection and reload data
      setSelectedRowKeys([]);
      setSelectedRows([]);
      loadData();
      
    } catch (error) {
      console.error('Error deleting selected videos:', error);
      message.error('Lỗi khi xóa videos đã chọn: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteAll = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      const response = await axios.delete('/api/video-tracking/remove-all', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        message.success(response.data.message);
        // Clear selection and reload data
        setSelectedRowKeys([]);
        setSelectedRows([]);
        loadData();
      } else {
        message.error(response.data.error || 'Lỗi khi xóa tất cả videos');
      }
      
    } catch (error) {
      console.error('Error deleting all videos:', error);
      message.error('Lỗi khi xóa tất cả videos: ' + (error.response?.data?.detail || error.message));
    }
  };

  const downloadVideo = async (videoUrl) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get('/api/tiktok-management/video/download', {
        params: { url: videoUrl, hd: 1 },
        headers: { Authorization: `Bearer tiktok-ai-huydev-2025` }
      });

      if (response.data && response.data.data) {
        window.open(response.data.data.play, '_blank');
        message.success('Đang tải video...');
      }
    } catch (error) {
      message.error('Lỗi khi tải video: ' + (error.response?.data?.detail || error.message));
    }
  };

  const loadComments = async (video) => {
    try {
      setCommentsLoading(true);
      setSelectedVideo(video);
      setCommentsModalVisible(true);
      
      if (!video.video_url) {
        message.error('Video URL không tồn tại');
        setCommentsLoading(false);
        return;
      }
      
      const requestData = {
        url: video.video_url,
        count: 50,
        cursor: 0
      };
      
      const response = await axios.post('/api/tiktok-management/video/comments', requestData, {
        headers: { 
          Authorization: `Bearer tiktok-ai-huydev-2025`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data && response.data.data && response.data.data.comments) {
        setComments(response.data.data.comments);
      } else {
        setComments([]);
        message.warning('Không có comments nào');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        message.error('Token không hợp lệ. Vui lòng đăng nhập lại.');
      } else {
        message.error('Lỗi khi tải comments: ' + (error.response?.data?.detail || error.message));
      }
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  };

  const columns = [
    {
      title: 'Thumbnail',
      dataIndex: 'cover_url',
      key: 'thumbnail',
      width: 120,
      render: (url, record) => (
        <div style={{ position: 'relative' }}>
          <Image
            src={url}
            alt="Video thumbnail"
            width={80}
            height={80}
            style={{ objectFit: 'cover', borderRadius: 8 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
          <PlayCircleOutlined 
            style={{ 
              position: 'absolute', 
              top: '50%', 
              left: '50%', 
              transform: 'translate(-50%, -50%)',
              fontSize: 20,
              color: 'white',
              textShadow: '0 0 4px rgba(0,0,0,0.8)'
            }} 
          />
        </div>
      ),
    },
    {
      title: 'Video Info',
      key: 'info',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            {record.title || 'No title'}
          </div>
          <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>
            ID: {record.video_id}
          </div>
          <div style={{ fontSize: 12, color: '#666' }}>
            Author: @{record.author_username || 'Unknown'}
          </div>
          {record.duration && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Duration: {Math.floor(record.duration / 60)}:{(record.duration % 60).toString().padStart(2, '0')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Statistics',
      key: 'stats',
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <div style={{ fontSize: 12 }}>
            <EyeOutlined /> {record.view_count?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <HeartOutlined /> {record.like_count?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <MessageOutlined /> {record.comment_count?.toLocaleString() || 0}
          </div>
          <div style={{ fontSize: 12 }}>
            <ShareAltOutlined /> {record.share_count?.toLocaleString() || 0}
          </div>
        </Space>
      ),
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_checked',
      key: 'last_checked',
      width: 120,
      render: (date) => (
        <div style={{ fontSize: 12 }}>
          {date ? new Date(date).toLocaleDateString('en-US') : 'Never'}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space direction="vertical" size="small">
          <Button 
            type="primary" 
            size="small" 
            icon={<ReloadOutlined />}
            onClick={() => handleUpdateVideo(record.id)}
          >
            Update
          </Button>
          <Button 
            size="small" 
            icon={<MessageOutlined />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              loadComments(record);
            }}
          >
            Comments
          </Button>
          <Button 
            size="small" 
            icon={<DownloadOutlined />}
            onClick={() => downloadVideo(record.video_url)}
          >
            Download
          </Button>
          <Popconfirm
            title="Remove video from tracking?"
            onConfirm={() => handleRemoveVideo(record.id)}
            okText="Remove"
            cancelText="Cancel"
          >
            <Button 
              size="small" 
              danger
              icon={<DeleteOutlined />}
            >
              Remove
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header */}
      <Card style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={3} style={{ margin: 0 }}>
              Video Tracking
            </Title>
            <Text type="secondary">Theo dõi và cập nhật thông tin video TikTok</Text>
          </Col>
          <Col>
            <Space>
              <Select
                value={timeFilter}
                onChange={setTimeFilter}
                style={{ width: 120 }}
                options={[
                  { value: 'all', label: 'All' },
                  { value: '1d', label: '1 Day' },
                  { value: '7d', label: '7 Days' },
                  { value: '1m', label: '1 Month' }
                ]}
              />
              <Button 
                icon={<ReloadOutlined />} 
                onClick={loadData}
                loading={loading}
              >
                Tải lại
              </Button>
              <Button 
                icon={<ReloadOutlined />}
                onClick={handleUpdateAllVideos}
                loading={loading}
                type="default"
              >
                Update All
              </Button>
              <Button 
                icon={<ImportOutlined />}
                onClick={handleBatchImport}
              >
                Batch Import
              </Button>
              {(selectedRowKeys.length > 0 || videos.length > 0) && (
                <Dropdown
                  menu={{
                    items: [
                      ...(selectedRowKeys.length > 0 ? [{
                        key: 'delete-selected',
                        label: (
                          <Popconfirm
                            title={`Xóa ${selectedRowKeys.length} video đã chọn?`}
                            onConfirm={handleDeleteSelected}
                            okText="Xóa"
                            cancelText="Hủy"
                            placement="left"
                          >
                            <span>Xóa đã chọn ({selectedRowKeys.length})</span>
                          </Popconfirm>
                        ),
                        icon: <DeleteOutlined />
                      }] : []),
                      ...(videos.length > 0 ? [{
                        key: 'delete-all',
                        label: (
                          <Popconfirm
                            title={`Xóa TẤT CẢ ${videos.length} videos? Hành động này không thể hoàn tác!`}
                            onConfirm={handleDeleteAll}
                            okText="Xóa tất cả"
                            cancelText="Hủy"
                            placement="left"
                          >
                            <span>Xóa tất cả ({videos.length})</span>
                          </Popconfirm>
                        ),
                        icon: <DeleteOutlined />
                      }] : [])
                    ]
                  }}
                  trigger={['click']}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    Xóa <DownOutlined />
                  </Button>
                </Dropdown>
              )}
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={() => setAddModalVisible(true)}
              >
                Thêm Video
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title={`Videos ${timeFilter === 'all' ? '(All)' : `(${timeFilter === '1d' ? '1 Day' : timeFilter === '7d' ? '7 Days' : '1 Month'})`}`}
              value={stats.total_videos || 0} 
              prefix={<PlayCircleOutlined />}
              suffix={stats.total_count && stats.total_count !== stats.total_videos ? `/ ${stats.total_count}` : ''}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Tổng Views" 
              value={stats.total_views || 0} 
              prefix={<EyeOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Tổng Likes" 
              value={stats.total_likes || 0} 
              prefix={<HeartOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="Accounts" 
              value={stats.accounts || 0} 
              prefix={<BarChartOutlined />}
            />
          </Card>
        </Col>
      </Row>


      {/* Videos Table */}
      <Card>
        <Table
          columns={columns}
          dataSource={videos}
          rowKey="id"
          rowSelection={{
            selectedRowKeys: selectedRowKeys,
            onChange: (selectedRowKeys, selectedRows) => {
              setSelectedRowKeys(selectedRowKeys);
              setSelectedRows(selectedRows);
            },
            getCheckboxProps: (record) => ({
              name: record.title,
            }),
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} của ${total} videos`,
          }}
        />
      </Card>

      {/* Add Video Modal */}
      <Modal
        title="Thêm Video Theo Dõi"
        open={addModalVisible}
        onCancel={() => {
          setAddModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddVideo}
        >
          <Form.Item
            name="video_url"
            label="TikTok Video URL"
            rules={[{ required: true, message: 'Vui lòng nhập URL video' }]}
          >
            <Input placeholder="https://vt.tiktok.com/..." />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Thêm Video
              </Button>
              <Button onClick={() => {
                setAddModalVisible(false);
                form.resetFields();
              }}>
                Hủy
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Comments Modal */}
      <Modal
        title={`Comments - ${selectedVideo?.title || 'Video'}`}
        open={commentsModalVisible}
        onCancel={() => {
          setCommentsModalVisible(false);
          setSelectedVideo(null);
          setComments([]);
        }}
        footer={null}
        width={800}
      >
        <Spin spinning={commentsLoading}>
          {comments.length > 0 ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {comments.map((comment, index) => {
                return (
                <Card key={index} size="small" style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Image
                      src={comment.user?.avatar_thumb?.url_list?.[0] || comment.user?.avatar_thumb || comment.user?.avatar || 'https://via.placeholder.com/40'}
                      alt="User avatar"
                      width={40}
                      height={40}
                      style={{ borderRadius: '50%', marginRight: 12 }}
                      fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
                    />
                    <div style={{ flex: 1, marginLeft: 12 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
                        {comment.user?.nickname || 'Anonymous'}
                      </div>
                      <div style={{ marginBottom: 4 }}>
                        {comment.text || 'No text'}
                      </div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {comment.create_time ? new Date(comment.create_time * 1000).toLocaleString() : 'Unknown time'}
                      </div>
                      {comment.digg_count > 0 && (
                        <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                          <HeartOutlined /> {comment.digg_count} likes
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
                );
              })}
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <MessageOutlined style={{ fontSize: 48, color: '#ccc', marginBottom: 16 }} />
              <div>No comments found</div>
            </div>
          )}
        </Spin>
      </Modal>

      {/* Batch Import Modal */}
      <Modal
        title="Batch Import Video URLs"
        open={batchModalVisible}
        onCancel={() => {
          setBatchModalVisible(false);
          batchForm.resetFields();
          setBatchVideoUrls('');
        }}
        footer={null}
        width={600}
      >
        <Form
          form={batchForm}
          layout="vertical"
          onFinish={handleBatchSubmit}
        >
          <Form.Item
            name="video_urls"
            label={<span style={{ color: 'red' }}>* Video URLs (one per line)</span>}
            rules={[{ required: true, message: 'Vui lòng nhập ít nhất một video URL' }]}
          >
            <TextArea
              rows={8}
              placeholder="Enter video URLs, one per line:"
              value={batchVideoUrls}
              onChange={handleBatchVideoUrlsChange}
              style={{ fontFamily: 'monospace' }}
            />
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">Or upload a text file:</Text>
            <Upload
              beforeUpload={handleFileUpload}
              accept=".txt"
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />} style={{ marginLeft: 8 }}>
                Upload TXT File
              </Button>
            </Upload>
          </div>

          <Form.Item style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                id="asyncImport"
                checked={useAsyncImport}
                onChange={(e) => setUseAsyncImport(e.target.checked)}
              />
              <label htmlFor="asyncImport" style={{ margin: 0 }}>
                <Text>
                  <strong>Background Import</strong> - Cho số lượng lớn (&gt;10 videos)
                </Text>
              </label>
            </div>
            {useAsyncImport && (
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 24 }}>
                Import sẽ chạy trong background, không cần chờ. Phù hợp cho {batchVideoUrls.split('\n').filter(line => line.trim() !== '').length} videos.
              </Text>
            )}
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={loading}
              >
                {useAsyncImport ? 'Start Background Import' : 'Import'} {batchVideoUrls.split('\n').filter(line => line.trim() !== '').length} Video URLs
              </Button>
              <Button onClick={() => {
                setBatchModalVisible(false);
                batchForm.resetFields();
                setBatchVideoUrls('');
              }}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VideoTracking;